import type { CatalogV2ListRow } from '@skupreme/api-contracts/core';
import { Badge, Cell, Column, Row, TableHeader } from '@skupreme/shared/ui';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';
import {
  UNASSIGNED_LABEL,
  useActiveScope,
} from '@features/account-view/active';
import {
  CatalogTableFrame,
  type CatalogTablePaging,
} from '../table/CatalogTableFrame';
import { AvailableUnitsHeader } from '../table/CatalogTable';
import { ItemProductCell } from '../table/ItemTitleCell';
import { useMarketSelection } from '../../hooks/use-market-selection';
import { formatV2Price } from '../../lib/v2';
import { CatalogAvailability } from './CatalogAvailability';
import { CatalogV2Variants, variantCountLabel } from './CatalogV2Variants';
import { MarketplaceChannels } from './MarketplaceChannels';

export type CatalogV2TableProps = CatalogTablePaging & {
  rows: CatalogV2ListRow[];
  /** EN-4396 Archive: the Archived view marks the rows that still hold available stock. */
  archivedView?: boolean;
};

const COLUMN_COUNT = 4;

/* EN-4396: the names of the divisions an item is listed in; none is Unassigned. */
const divisionsOf = (row: CatalogV2ListRow) =>
  row.divisions?.length
    ? row.divisions.map(({ name }) => name).join(', ')
    : UNASSIGNED_LABEL;

const detailPath = (id: string) =>
  `/app/catalogs/catalog/${encodeURIComponent(id)}`;

function ProductCell({
  row,
  expand,
}: {
  row: CatalogV2ListRow;
  /** The parent's expand control; none for an item without variants. */
  expand?: ReactNode;
}) {
  return (
    <ItemProductCell
      item={{
        _id: row.id,
        p_title: row.title,
        p_sku: row.sku ?? undefined,
        secondary_identifier: row.asin,
      }}
      imageUrl={row.image_url}
      identifiers={[{ label: 'GTIN', value: row.gtin ?? undefined }]}
      showState={false}
      expand={expand}
    >
      {row.variant_count > 0 ? (
        <span className="sk:text-xs sk:text-fg-tertiary">
          {variantCountLabel(row.variant_count)}
        </span>
      ) : null}
    </ItemProductCell>
  );
}

/** The V2 catalog list: read-only, so no tags, actions or selection; the type is on the detail page. */
export function CatalogV2Table({
  rows,
  archivedView = false,
  ...paging
}: CatalogV2TableProps) {
  const navigate = useNavigate();
  /* EN-4396: the account level adds the division, and the split of the available units. */
  const { level } = useActiveScope();
  const isAccount = level === 'account';
  const showDivision = isAccount && rows.every(row => !!row.divisions);
  const columnCount = COLUMN_COUNT + (showDivision ? 1 : 0);
  /* M2: with countries picked, only their marketplace logos. */
  const { countries } = useMarketSelection();

  const renderRow = (row: CatalogV2ListRow, expand?: ReactNode) => (
    <Row key={row.id} id={row.id} onAction={() => navigate(detailPath(row.id))}>
      <Cell>
        <ProductCell row={row} expand={expand} />
      </Cell>
      {showDivision ? <Cell>{divisionsOf(row)}</Cell> : null}
      <Cell>
        <span className="sk:whitespace-nowrap sk:tabular-nums">
          {formatV2Price(row.price) ?? '—'}
        </span>
      </Cell>
      <Cell>
        <div className="sk:flex sk:flex-col sk:items-start sk:gap-1">
          <CatalogAvailability row={row} />
          {archivedView && (row.available ?? 0) > 0 ? (
            <Badge tone="caution">Has stock</Badge>
          ) : null}
        </div>
      </Cell>
      <Cell>
        <MarketplaceChannels
          channels={row.channels}
          item={row}
          countries={countries}
        />
      </Cell>
    </Row>
  );

  return (
    <CatalogTableFrame
      aria-label="Catalog"
      columnCount={columnCount}
      header={
        <TableHeader>
          <Column
            id="title"
            isRowHeader
            allowsSorting
            className={showDivision ? 'sk:w-[48%]' : 'sk:w-[59%]'}
          >
            Product
          </Column>
          {showDivision ? (
            <Column id="division" className="sk:w-[12%]">
              Division
            </Column>
          ) : null}
          <Column
            id="price"
            className={showDivision ? 'sk:w-[8%]' : 'sk:w-[9%]'}
          >
            Price
          </Column>
          <Column id="available" allowsSorting className="sk:w-[13%]">
            <AvailableUnitsHeader />
          </Column>
          <Column id="channels" className="sk:w-[19%]">
            Marketplaces
          </Column>
        </TableHeader>
      }
      rows={rows.map(row =>
        row.variant_count > 0 ? (
          <CatalogV2Variants
            key={row.id}
            row={row}
            columnCount={columnCount}
            renderParent={expand => renderRow(row, expand)}
          />
        ) : (
          renderRow(row)
        ),
      )}
      {...paging}
    />
  );
}
